import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Dropdown, Form, InputGroup, Row } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';
import useStudyYearSelector from './useStudyYearSelector';
import ThaiDateInput from '../components/ThaiDateInput'
import {
  durationText,
  FieldLabel,
  GUARDIAN_RELATIONS,
  MultiOptionButtons,
  OTHER_OPTION,
  OptionButtons,
  OUTING_REASON_OPTIONS,
  PermissionButtons,
  QuickDateChips,
  RequestList,
  STATUS_ACTION_LABEL,
  StatusFilterChips,
  StudentPicker,
  statusBadge,
  thaiDate,
  thaiTime,
  todayISO
} from './leaveOptions';

const toastSuccess = (title, text) =>
  Swal.fire({ icon: 'success', title, text, timer: 1600, showConfirmButton: false });

const alertError = (title, err) =>
  Swal.fire({ icon: 'error', title, text: err?.response?.data?.message || '' });

const emptyForm = () => ({
  outingDate: todayISO(),
  outTime: '08:30',
  expectReturnTime: '16:00',
  destination: '',
  reason: '',
  reasonOther: '',
  guardianRelations: [],
  guardianRelationOther: '',
  guardianName: '',
  guardianPhone: '',
  status: 'approved',
  note: ''
});

// สถานะการผ่านประตูของใบออกนอกสถานที่ — ฝ่ายปกครองใช้ดูว่ากลับเข้าครบหรือยัง
const gateLabel = (req) => {
  if (req.checked_in_at) return { variant: 'success', text: 'กลับเข้าแล้ว' };
  if (req.checked_out_at) return { variant: 'warning', text: 'ออกแล้ว รอกลับเข้า' };
  return null;
};

export default function DisciplineOuting() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();

  // ค้นหานักเรียน (ฝั่งซ้าย) กับค้นหาในรายการคำขอ (ฝั่งขวา) แยกกัน — เดิมใช้ช่องเดียวกันจึงสับสน
  const [studentKeyword, setStudentKeyword] = useState('');
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [searchedTerm, setSearchedTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [listKeyword, setListKeyword] = useState('');
  const [listTerm, setListTerm] = useState('');
  const [requests, setRequests] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [status, setStatus] = useState('all');
  const [busyId, setBusyId] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  // เปลี่ยนปีการศึกษาแล้วผลค้นหาเดิมใช้ไม่ได้ ต้องค้นใหม่
  useEffect(() => {
    setSelectedStudent(null);
    setStudents([]);
    setSearchedTerm('');
  }, [yearId]);

  useEffect(() => {
    const timer = setTimeout(() => setListTerm(listKeyword.trim()), 350);
    return () => clearTimeout(timer);
  }, [listKeyword]);

  // ค้นเมื่อกดปุ่มเท่านั้น — ไม่ดึงรายชื่อนักเรียนทั้งโรงเรียนตอนเปิดหน้า
  const searchStudents = useCallback((e) => {
    e?.preventDefault();
    const term = studentKeyword.trim();
    if (!yearId) return;
    if (!term) {
      Swal.fire({ icon: 'info', title: 'กรุณาป้อนเลขประจำตัวหรือชื่อนักเรียน', timer: 1600, showConfirmButton: false });
      return;
    }
    setLoadingStudents(true);
    axiosInstance.get(`/api/discipline/conduct/students?yearId=${yearId}&keyword=${encodeURIComponent(term)}`)
      .then(res => {
        setStudents(res.data || []);
        setSearchedTerm(term);
      })
      .catch(err => alertError('ไม่สามารถค้นหานักเรียนได้', err))
      .finally(() => setLoadingStudents(false));
  }, [yearId, studentKeyword]);

  const fetchRequests = useCallback(() => {
    if (!yearId) return;
    setLoadingList(true);
    const params = new URLSearchParams({ yearId, status });
    if (listTerm) params.set('keyword', listTerm);
    axiosInstance.get(`/api/discipline/outing?${params.toString()}`)
      .then(res => setRequests(res.data || []))
      .catch(err => alertError('ไม่สามารถโหลดคำขอออกนอกสถานที่ได้', err))
      .finally(() => setLoadingList(false));
  }, [yearId, status, listTerm]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const reasonText = (form.reason === OTHER_OPTION ? form.reasonOther : form.reason).trim();
  const reasonMissing = submitted && !reasonText;
  // ใบนี้เป็นแบบไป-กลับวันเดียว เวลากลับต้องหลังเวลาออกเสมอ — เช็คสดๆ ไม่ต้องรอกดบันทึก
  const timeInvalid = !!form.outTime && !!form.expectReturnTime && form.outTime >= form.expectReturnTime;

  const resetForm = () => {
    setForm(emptyForm());
    setSubmitted(false);
  };

  // ผู้รับเลือกได้หลายคน เช่น พ่อกับแม่มารับพร้อมกัน
  const toggleRelation = (value) => setForm(prev => {
    const values = prev.guardianRelations.includes(value)
      ? prev.guardianRelations.filter(item => item !== value)
      : [...prev.guardianRelations, value];
    return {
      ...prev,
      guardianRelations: values,
      guardianRelationOther: values.includes(OTHER_OPTION) ? prev.guardianRelationOther : ''
    };
  });

  // "อื่นๆ" ถูกแทนที่ด้วยข้อความที่ผู้ใช้พิมพ์ แล้วต่อกันด้วย / เช่น "พ่อ/แม่"
  const relationText = () => form.guardianRelations
    .map(item => (item === OTHER_OPTION ? form.guardianRelationOther.trim() : item))
    .filter(Boolean)
    .join('/');

  // กด "เปลี่ยน" = เริ่มใหม่หมด ทั้งนักเรียนที่เลือก ฟอร์ม และช่องค้นหา
  const clearStudent = () => {
    setSelectedStudent(null);
    setStudents([]);
    setSearchedTerm('');
    setStudentKeyword('');
    resetForm();
  };

  const createRequest = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!selectedStudent) {
      Swal.fire({ icon: 'warning', title: 'กรุณาเลือกนักเรียน', timer: 1600, showConfirmButton: false });
      return;
    }
    if (!reasonText) {
      Swal.fire({ icon: 'warning', title: 'กรุณาเลือกหรือระบุเหตุผล', timer: 1600, showConfirmButton: false });
      return;
    }
    if (timeInvalid) {
      Swal.fire({
        icon: 'warning',
        title: 'เวลากลับต้องหลังเวลาออก',
        text: 'ใบขออนุญาตนี้เป็นแบบไป-กลับภายในวันเดียวกัน'
      });
      return;
    }
    const guardianName = [relationText(), form.guardianName.trim()].filter(Boolean).join(' ');
    setSaving(true);
    axiosInstance.post('/api/discipline/outing', {
      studentId: selectedStudent.st_id,
      studyId: selectedStudent.study_id,
      yearId,
      outingDate: form.outingDate,
      outTime: form.outTime,
      expectReturnTime: form.expectReturnTime,
      destination: form.destination.trim(),
      reason: reasonText,
      guardianName,
      guardianPhone: form.guardianPhone,
      status: form.status,
      note: form.note
    }).then(() => {
      resetForm();
      fetchRequests();
      toastSuccess('บันทึกคำขอออกนอกสถานที่แล้ว', selectedStudent.st_name);
    }).catch(err => alertError('ไม่สามารถบันทึกได้', err))
      .finally(() => setSaving(false));
  };

  // การกระทำที่ย้อนกลับไม่ได้ (ปฏิเสธ/ยกเลิก) ถามยืนยันก่อนเสมอ
  const updateStatus = async (req, nextStatus) => {
    const label = STATUS_ACTION_LABEL[nextStatus] || 'อัปเดตสถานะ';
    if (nextStatus === 'rejected' || nextStatus === 'cancelled') {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: `${label}คำขอนี้?`,
        html: `<div class="fw-semibold">${req.st_name}</div>`
          + `<div>${thaiDate(req.outing_date)} ${req.out_time} - ${req.expect_return_time} น.</div>`
          // ผ่านประตูไปแล้วแต่ยังยกเลิกได้ — เตือนให้รู้ตัวก่อนกด
          + (req.checked_out_at ? `<div class="text-danger mt-2">นักเรียนออกจากโรงเรียนไปแล้ว (${thaiTime(req.checked_out_at)} น.)</div>` : ''),
        showCancelButton: true,
        confirmButtonText: label,
        cancelButtonText: 'ปิด',
        confirmButtonColor: '#dc3545',
        reverseButtons: true
      });
      if (!confirm.isConfirmed) return;
    }
    setBusyId(req.out_id);
    try {
      await axiosInstance.put('/api/discipline/outing', { outId: req.out_id, status: nextStatus });
      fetchRequests();
      toastSuccess(`${label}เรียบร้อย`);
    } catch (err) {
      alertError(`ไม่สามารถ${label}ได้`, err);
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      header: 'วันที่ / เวลา',
      cell: req => (
        <>
          <div>{thaiDate(req.outing_date)}</div>
          <div className="fs-xs text-secondary">
            {req.out_time} - {req.expect_return_time} น.
            {durationText(req.out_time, req.expect_return_time) && ` (${durationText(req.out_time, req.expect_return_time)})`}
          </div>
        </>
      )
    },
    {
      header: 'สถานที่ / เหตุผล',
      cell: req => (
        <>
          {req.destination && <div>{req.destination}</div>}
          <div className={req.destination ? 'fs-xs text-secondary' : ''}>{req.reason}</div>
          <div className="fs-xs text-secondary">
            <i className="ri-user-line me-1"></i>{req.guardian_name || 'ไม่ระบุผู้รับ'} {req.guardian_phone || ''}
          </div>
          {req.note && <div className="fs-xs text-secondary">หมายเหตุ: {req.note}</div>}
        </>
      )
    }
  ];

  const statusCell = req => {
    const gate = gateLabel(req);
    return (
      <>
        {statusBadge(req.status)}
        {gate && (
          <div className={`fs-xs text-${gate.variant} mt-1`}>
            {gate.text}
            <div className="text-secondary">
              {thaiTime(req.checked_out_at)}{req.checked_in_at ? ` - ${thaiTime(req.checked_in_at)}` : ''} น.
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ฝ่ายปกครอง</li>
              <li className="breadcrumb-item active">ออกนอกสถานที่</li>
            </ol>
            <h4 className="main-title mb-0">คำขออนุญาตออกนอกสถานที่</h4>
            <div className="fs-xs text-secondary">ไป-กลับภายในวันเดียวกัน · ยามจะเห็นใบนี้ที่หน้าประตูทั้งตอนออกและตอนกลับเข้า</div>
          </div>
          <Dropdown className="mt-3 mt-md-0">
            <Dropdown.Toggle variant="primary" id="discipline-outing-year">
              <i className="ri-calendar-line me-1"></i>{yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(year => (
                <Dropdown.Item key={year.year_id} onClick={() => handleSelectYear(year.year_id)}>
                  {`ปีการศึกษา ${year.year_year}`}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <Row className="g-3">
          <Col xl={4}>
            {/* ค้นหานักเรียนกับฟอร์มอยู่การ์ดเดียวกัน — เลือกนักเรียนแล้วฟอร์มจะต่อท้ายทันที */}
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">บันทึกคำขอออกนอกสถานที่</Card.Title>
                {selectedStudent && (
                  <Button variant="link" size="sm" className="p-0 ms-auto" onClick={resetForm}>ล้างฟอร์ม</Button>
                )}
              </Card.Header>
              <Card.Body>
                <StudentPicker
                  keyword={studentKeyword}
                  onKeywordChange={setStudentKeyword}
                  onSubmit={searchStudents}
                  students={students}
                  loading={loadingStudents}
                  selected={selectedStudent}
                  onSelect={setSelectedStudent}
                  searchedTerm={searchedTerm}
                  onClear={clearStudent}
                />
                {!selectedStudent ? (
                  <div className="text-center text-secondary fs-xs border-top pt-3 mt-1">
                    เลือกนักเรียนก่อน แล้วฟอร์มบันทึกคำขอจะปรากฏที่นี่
                  </div>
                ) : (
                  <Form onSubmit={createRequest}>
                    <Row className="g-3">
                      <Col xs={12}>
                        <FieldLabel required hint="ไป-กลับวันเดียวกัน">วันที่ออก</FieldLabel>
                        <ThaiDateInput
                          value={form.outingDate}
                          onChange={v => setForm({ ...form, outingDate: v })}
                          required
                        />
                        <QuickDateChips value={form.outingDate} onSelect={value => setForm({ ...form, outingDate: value })} />
                      </Col>
                      <Col xs={6}>
                        <FieldLabel required>เวลาออก</FieldLabel>
                        <Form.Control
                          type="time"
                          value={form.outTime}
                          onChange={e => setForm({ ...form, outTime: e.target.value })}
                          isInvalid={timeInvalid}
                          required
                        />
                      </Col>
                      <Col xs={6}>
                        <FieldLabel required hint="โดยประมาณ">เวลากลับ</FieldLabel>
                        <Form.Control
                          type="time"
                          value={form.expectReturnTime}
                          onChange={e => setForm({ ...form, expectReturnTime: e.target.value })}
                          isInvalid={timeInvalid}
                          required
                        />
                      </Col>
                      <Col xs={12} className="pt-0">
                        {timeInvalid ? (
                          <div className="fs-xs text-danger">เวลากลับต้องหลังเวลาออก</div>
                        ) : (
                          <div className="fs-xs text-secondary">
                            ออกนอกโรงเรียนประมาณ {durationText(form.outTime, form.expectReturnTime) || '-'}
                          </div>
                        )}
                      </Col>
                      <Col xs={12}>
                        <FieldLabel hint="ไม่ระบุก็ได้">สถานที่ที่จะไป</FieldLabel>
                        <Form.Control
                          value={form.destination}
                          onChange={e => setForm({ ...form, destination: e.target.value })}
                          placeholder="เช่น โรงพยาบาลนราธิวาสราชนครินทร์"
                        />
                      </Col>
                      <Col xs={12}>
                        <FieldLabel required>เหตุผล</FieldLabel>
                        <OptionButtons
                          options={OUTING_REASON_OPTIONS}
                          value={form.reason}
                          invalid={reasonMissing}
                          onSelect={value => setForm({ ...form, reason: value, reasonOther: value === OTHER_OPTION ? form.reasonOther : '' })}
                        />
                        {form.reason === OTHER_OPTION && (
                          <Form.Control
                            className="mt-2"
                            as="textarea"
                            rows={2}
                            value={form.reasonOther}
                            onChange={e => setForm({ ...form, reasonOther: e.target.value })}
                            placeholder="ระบุเหตุผล"
                            required
                          />
                        )}
                        {reasonMissing && <div className="fs-xs text-danger mt-1">กรุณาเลือกหรือระบุเหตุผล</div>}
                      </Col>
                      <Col xs={12}>
                        <FieldLabel hint="เลือกได้มากกว่า 1 · ชื่อไม่ระบุก็ได้">ผู้รับ/ผู้ปกครอง</FieldLabel>
                        <MultiOptionButtons
                          options={GUARDIAN_RELATIONS}
                          values={form.guardianRelations}
                          onToggle={toggleRelation}
                        />
                        {form.guardianRelations.includes(OTHER_OPTION) && (
                          <Form.Control
                            className="mt-2"
                            value={form.guardianRelationOther}
                            onChange={e => setForm({ ...form, guardianRelationOther: e.target.value })}
                            placeholder="ระบุความสัมพันธ์"
                            required
                          />
                        )}
                        <Row className="g-2 mt-0">
                          <Col md={7}>
                            <Form.Control
                              value={form.guardianName}
                              onChange={e => setForm({ ...form, guardianName: e.target.value })}
                              placeholder="ชื่อ-สกุลผู้รับ"
                            />
                          </Col>
                          <Col md={5}>
                            <Form.Control
                              type="tel"
                              inputMode="tel"
                              value={form.guardianPhone}
                              onChange={e => setForm({ ...form, guardianPhone: e.target.value })}
                              placeholder="เบอร์ติดต่อ"
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col xs={12}>
                        <FieldLabel>ผลการพิจารณา</FieldLabel>
                        <PermissionButtons value={form.status} onSelect={value => setForm({ ...form, status: value })} />
                      </Col>
                      <Col xs={12}>
                        <FieldLabel hint="ไม่ระบุก็ได้">หมายเหตุ</FieldLabel>
                        <Form.Control value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
                      </Col>
                      <Col xs={12} className="d-grid">
                        <Button type="submit" size="lg" disabled={saving || timeInvalid}>
                          <i className="ri-save-line me-1"></i>
                          {saving ? 'กำลังบันทึก...' : 'บันทึกคำขอ'}
                        </Button>
                        <div className="fs-xs text-secondary text-center mt-2">
                          {selectedStudent.st_name} · {thaiDate(form.outingDate)} {form.outTime} - {form.expectReturnTime} น.
                          {reasonText ? ` · ${reasonText}` : ''}
                        </div>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={8}>
            <Card className="card-one">
              <Card.Header className="d-block">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Card.Title as="h6" className="mb-0">
                    รายการคำขอ <Badge bg="secondary" className="ms-1">{requests.length}</Badge>
                  </Card.Title>
                  <InputGroup size="sm" className="ms-auto" style={{ width: 240 }}>
                    <Form.Control
                      value={listKeyword}
                      onChange={e => setListKeyword(e.target.value)}
                      placeholder="ค้นหาในรายการ"
                      inputMode="search"
                    />
                    {listKeyword && (
                      <Button variant="outline-secondary" onClick={() => setListKeyword('')} aria-label="ล้างคำค้นหา">
                        <i className="ri-close-line"></i>
                      </Button>
                    )}
                  </InputGroup>
                  <Button variant="outline-secondary" size="sm" onClick={fetchRequests} aria-label="รีเฟรช">
                    <i className="ri-refresh-line"></i>
                  </Button>
                </div>
                <div className="mt-2">
                  <StatusFilterChips value={status} onSelect={setStatus} />
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <RequestList
                  rows={requests}
                  loading={loadingList}
                  keyOf={req => req.out_id}
                  columns={columns}
                  statusCell={statusCell}
                  onUpdate={updateStatus}
                  busyKey={busyId}
                  emptyText={listTerm ? `ไม่พบคำขอที่ตรงกับ “${listTerm}”` : 'ยังไม่มีรายการคำขอ'}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </>
  );
}
